/**
 * Constants for audio waveform comparison and frequency analysis.
 * These values control thresholds, weights, and tolerances used in audio similarity detection.
 */

/**
 * Hamming window coefficient for the constant term.
 * Used in windowing function to reduce spectral leakage in FFT analysis.
 */
const HAMMING_WINDOW_CONSTANT = 0.54;

/**
 * Hamming window coefficient for the cosine term.
 * Used in windowing function to reduce spectral leakage in FFT analysis.
 */
const HAMMING_WINDOW_COSINE_COEFFICIENT = 0.46;

/**
 * Threshold for energy concentration in frequency analysis.
 * Frequencies must have at least 85% of total energy to be considered dominant.
 */
const DOMINANT_FREQUENCY_ENERGY_THRESHOLD = 0.85;

/**
 * Relative threshold multiplier for finding dominant frequencies.
 * Frequencies must be at least 1% of the maximum magnitude to be considered.
 */
const DOMINANT_FREQUENCY_RELATIVE_THRESHOLD_MULTIPLIER = 0.01;

/**
 * Minimum RMS (Root Mean Square) threshold for mobile devices.
 * Recorded audio below this threshold is considered too quiet/noisy.
 * Value: 0.004
 */
const MIN_RMS_THRESHOLD_MOBILE = 0.004;

/**
 * Minimum RMS (Root Mean Square) threshold for Safari browsers.
 * Safari records audio at lower levels, so we use a more lenient threshold.
 * Value: 0.0001 (very lenient to account for Safari's lower recording levels)
 */
const MIN_RMS_THRESHOLD_SAFARI = 0.0001;

/**
 * Minimum RMS (Root Mean Square) threshold for desktop devices.
 * Recorded audio below this threshold is considered too quiet/noisy.
 * Value: 0.008
 */
const MIN_RMS_THRESHOLD_DESKTOP = 0.008;

/**
 * Minimum value used as fallback when calculating RMS ratio to prevent division by zero.
 * Used when reference RMS is zero or very small.
 * Value: 0.001
 */
const MIN_RMS_FALLBACK = 0.001;

/**
 * Minimum RMS ratio threshold for low mobile threshold scenarios.
 * Recorded RMS must be at least 10% of reference RMS to be considered valid.
 * Value: 0.1
 */
const MIN_RMS_RATIO_LOW_MOBILE = 0.1;

/**
 * Minimum RMS ratio threshold for standard mobile/desktop scenarios.
 * Recorded RMS must be at least 18% of reference RMS to be considered valid.
 * Value: 0.18
 */
const MIN_RMS_RATIO_STANDARD = 0.18;

/**
 * Minimum RMS ratio threshold for Safari browsers.
 * Safari records audio at lower levels, so we use a more lenient threshold.
 * Recorded RMS must be at least 0.1% of reference RMS to be considered valid.
 * Value: 0.001 (very lenient to account for Safari's lower recording levels)
 */
const MIN_RMS_RATIO_SAFARI = 0.001;

/**
 * Maximum RMS ratio threshold for low mobile threshold scenarios.
 * Recorded RMS must not exceed 800% of reference RMS to be considered valid.
 * Value: 8
 */
const MAX_RMS_RATIO_LOW_MOBILE = 8;

/**
 * Maximum RMS ratio threshold for standard mobile/desktop scenarios.
 * Recorded RMS must not exceed 500% of reference RMS to be considered valid.
 * Value: 5
 */
const MAX_RMS_RATIO_STANDARD = 5;

/**
 * Maximum RMS ratio threshold for Safari browsers.
 * Safari records audio at lower levels, so we use a more lenient threshold.
 * Recorded RMS must not exceed 2000% of reference RMS to be considered valid.
 * Value: 20 (increased to account for Safari's variable recording levels)
 */
const MAX_RMS_RATIO_SAFARI = 20;

/**
 * Weight for frame similarity in mobile devices when calculating combined similarity score.
 * Value: 0.4 (40% weight)
 */
const FRAME_SIMILARITY_WEIGHT_MOBILE = 0.4;

/**
 * Weight for frame similarity in desktop devices when calculating combined similarity score.
 * Value: 0.5 (50% weight)
 */
const FRAME_SIMILARITY_WEIGHT_DESKTOP = 0.5;

/**
 * Weight for average spectrum similarity in mobile devices when calculating combined similarity
 * score. Value: 0.25 (25% weight)
 */
const AVG_SPECTRUM_WEIGHT_MOBILE = 0.25;

/**
 * Weight for average spectrum similarity in desktop devices when calculating combined similarity
 * score. Value: 0.2 (20% weight)
 */
const AVG_SPECTRUM_WEIGHT_DESKTOP = 0.2;

/**
 * Weight for dominant frequency similarity in mobile devices when calculating combined similarity
 * score. Value: 0.35 (35% weight)
 */
const DOMINANT_FREQUENCY_WEIGHT_MOBILE = 0.35;

/**
 * Weight for dominant frequency similarity in desktop devices when calculating combined similarity
 * score. Value: 0.3 (30% weight)
 */
const DOMINANT_FREQUENCY_WEIGHT_DESKTOP = 0.3;

/**
 * Default similarity threshold for audio comparison.
 * Audio is considered similar if combined similarity score is at least 65%.
 * Value: 0.65
 */
const DEFAULT_SIMILARITY_THRESHOLD = 0.65;

/**
 * Threshold to determine if mobile device should use low threshold mode.
 * If mobileSimilarityThreshold is below this value, more lenient thresholds are used.
 * Value: 0.3
 */
const LOW_MOBILE_THRESHOLD_CUTOFF = 0.3;

/**
 * Adjustment value subtracted from similarity threshold for mobile devices.
 * Used to calculate adjusted threshold: max(similarityThreshold - 0.15, 0.35)
 * Value: 0.15
 */
const MOBILE_SIMILARITY_THRESHOLD_ADJUSTMENT = 0.15;

/**
 * Minimum similarity threshold for mobile devices after adjustment.
 * Used as fallback when adjusted threshold would be too low.
 * Value: 0.32
 */
const MIN_MOBILE_SIMILARITY_THRESHOLD = 0.32;

/**
 * Minimum duration ratio for low mobile threshold scenarios.
 * Recorded audio duration must be at least 20% of reference duration.
 * Value: 0.2
 */
const MIN_DURATION_RATIO_LOW_MOBILE = 0.2;

/**
 * Minimum duration ratio for standard mobile scenarios.
 * Recorded audio duration must be at least 30% of reference duration.
 * Value: 0.3
 */
const MIN_DURATION_RATIO_MOBILE = 0.3;

/**
 * Minimum duration ratio for desktop scenarios.
 * Recorded audio duration must be at least 40% of reference duration.
 * Value: 0.4
 */
const MIN_DURATION_RATIO_DESKTOP = 0.4;

/**
 * Minimum frame similarity threshold for low mobile threshold scenarios.
 * Best frame similarity must be at least 25% to pass validation.
 * Value: 0.25
 */
const MIN_FRAME_SIMILARITY_LOW_MOBILE = 0.25;

/**
 * Minimum frame similarity threshold for standard mobile scenarios.
 * Best frame similarity must be at least 40% to pass validation.
 * Value: 0.4
 */
const MIN_FRAME_SIMILARITY_MOBILE = 0.4;

/**
 * Minimum frame similarity threshold for desktop scenarios.
 * Best frame similarity must be at least 50% to pass validation.
 * Value: 0.5
 */
const MIN_FRAME_SIMILARITY_DESKTOP = 0.5;

/**
 * Minimum dominant frequency similarity threshold for low mobile threshold scenarios.
 * Dominant frequency similarity must be at least 25% to pass validation.
 * Value: 0.25
 */
const MIN_DOMINANT_FREQUENCY_SIMILARITY_LOW_MOBILE = 0.25;

/**
 * Minimum dominant frequency similarity threshold for standard mobile scenarios.
 * Dominant frequency similarity must be at least 35% to pass validation.
 * Value: 0.35
 */
const MIN_DOMINANT_FREQUENCY_SIMILARITY_MOBILE = 0.35;

/**
 * Minimum dominant frequency similarity threshold for desktop scenarios.
 * Dominant frequency similarity must be at least 45% to pass validation.
 * Value: 0.45
 */
const MIN_DOMINANT_FREQUENCY_SIMILARITY_DESKTOP = 0.45;

/**
 * Minimum average spectrum similarity threshold for low mobile threshold scenarios.
 * Average spectrum similarity must be at least 25% to pass validation.
 * Value: 0.25
 */
const MIN_AVG_SPECTRUM_SIMILARITY_LOW_MOBILE = 0.25;

/**
 * Minimum average spectrum similarity threshold for standard mobile scenarios.
 * Average spectrum similarity must be at least 35% to pass validation.
 * Value: 0.35
 */
const MIN_AVG_SPECTRUM_SIMILARITY_MOBILE = 0.35;

/**
 * Minimum average spectrum similarity threshold for desktop scenarios.
 * Average spectrum similarity must be at least 45% to pass validation.
 * Value: 0.45
 */
const MIN_AVG_SPECTRUM_SIMILARITY_DESKTOP = 0.45;

/**
 * Minimum overall similarity score required for audio to be considered similar.
 * Combined similarity must be at least 10% regardless of other thresholds.
 * Value: 0.1
 */
const MIN_OVERALL_SIMILARITY = 0.1;

/**
 * Safari high similarity threshold for bypassing strict frequency matching.
 * If Safari browser has combined similarity >= 50%, it can bypass top frequency validation.
 * Value: 0.5
 */
const SAFARI_HIGH_SIMILARITY_THRESHOLD = 0.5;

/**
 * Safari RMS fallback multiplier for low RMS scenarios.
 * Safari can continue processing if RMS is at least 50% of minimum threshold.
 * Value: 0.5
 */
const SAFARI_RMS_FALLBACK_MULTIPLIER = 0.5;

/**
 * Noise detection similarity multiplier.
 * Similarity score below 30% of threshold indicates noise rather than correct audio.
 * Value: 0.3
 */
const NOISE_DETECTION_SIMILARITY_MULTIPLIER = 0.3;

/**
 * Reasonable similarity multiplier for early return check.
 * If similarity is >= 60% of threshold, can bypass strict top frequency validation.
 * Value: 0.6
 */
const REASONABLE_SIMILARITY_EARLY_RETURN_MULTIPLIER = 0.6;

/**
 * Reasonable similarity multiplier for final similarity check.
 * If similarity is >= 70% of threshold, can bypass strict top frequency matching requirement.
 * Value: 0.7
 */
const REASONABLE_SIMILARITY_FINAL_CHECK_MULTIPLIER = 0.7;

/**
 * Low threshold adjustment multiplier for top frequency matches.
 * When threshold is low, reduce minimum top frequency matches to 60% of normal requirement.
 * Value: 0.6
 */
const LOW_THRESHOLD_TOP_FREQ_MATCHES_MULTIPLIER = 0.6;

/**
 * Low threshold adjustment multiplier for duration ratio.
 * When threshold is low, reduce minimum duration ratio to 60% of normal requirement.
 * Value: 0.6
 */
const LOW_THRESHOLD_DURATION_RATIO_MULTIPLIER = 0.6;

/**
 * Minimum threshold scale for low threshold scenarios.
 * Ensures threshold scaling doesn't go below 60% when calculating adjusted thresholds.
 * Value: 0.6
 */
const MIN_THRESHOLD_SCALE_FOR_LOW_THRESHOLD = 0.6;

/**
 * Minimum duration ratio floor for low threshold scenarios.
 * Absolute minimum duration ratio allowed, regardless of other adjustments.
 * Value: 0.12
 */
const MIN_DURATION_RATIO_FLOOR_LOW_THRESHOLD = 0.12;

/**
 * Minimum component similarity floor for low threshold scenarios.
 * Absolute minimum for frame, dominant frequency, and spectrum similarity.
 * Value: 0.15
 */
const MIN_COMPONENT_SIMILARITY_FLOOR_LOW_THRESHOLD = 0.15;

/**
 * Minimum top frequency matches for low threshold scenarios.
 * At least 2 frequencies must match when using low thresholds.
 * Value: 2
 */
const MIN_TOP_FREQUENCY_MATCHES_FLOOR_LOW_THRESHOLD = 2;

/**
 * Minimum top frequency matches required for low threshold scenarios.
 * Ensures at least 3 frequencies match even for low thresholds to prevent false positives.
 * Value: 3
 */
const MIN_TOP_FREQUENCY_MATCHES_REQUIRED_LOW_THRESHOLD = 3;

/**
 * Dominant frequency similarity threshold for low threshold mobile scenarios.
 * Mobile devices with low thresholds require at least 20% dominant frequency similarity.
 * Value: 0.2
 */
const DOMINANT_FREQ_SIMILARITY_LOW_THRESHOLD_MOBILE = 0.2;

/**
 * Dominant frequency similarity threshold for low threshold desktop scenarios.
 * Desktop devices with low thresholds require at least 25% dominant frequency similarity.
 * Value: 0.25
 */
const DOMINANT_FREQ_SIMILARITY_LOW_THRESHOLD_DESKTOP = 0.25;

/**
 * Dominant frequency similarity threshold for normal threshold mobile scenarios.
 * Mobile devices with normal thresholds require at least 30% dominant frequency similarity.
 * Value: 0.3
 */
const DOMINANT_FREQ_SIMILARITY_NORMAL_THRESHOLD_MOBILE = 0.3;

/**
 * Dominant frequency similarity threshold for normal threshold desktop scenarios.
 * Desktop devices with normal thresholds require at least 40% dominant frequency similarity.
 * Value: 0.4
 */
const DOMINANT_FREQ_SIMILARITY_NORMAL_THRESHOLD_DESKTOP = 0.4;

/**
 * Minimum top frequency matches required for top2MustMatch validation.
 * Top 2 frequencies must match when minimum matches requirement is >= 3.
 * Value: 3
 */
const MIN_TOP_MATCHES_FOR_TOP2_VALIDATION = 3;

/**
 * Number of top frequencies to consider for top2MustMatch validation.
 * Validates the top 5 frequencies for matching.
 * Value: 5
 */
const TOP_FREQUENCIES_COUNT_FOR_VALIDATION = 5;

/**
 * Number of top frequencies to compare in dominant frequency comparison.
 * Compares the top 10 frequencies for similarity calculation.
 * Value: 10
 */
const TOP_FREQUENCIES_COUNT_FOR_COMPARISON = 10;

/**
 * FFT size for frequency spectrum analysis.
 * Standard FFT size used for audio frequency analysis.
 * Value: 2048
 */
const FFT_SIZE = 2048;

/**
 * Very high similarity multiplier for bypassing top frequency matching.
 * If similarity is >= 150% of threshold with high component scores,
 * can bypass strict top frequency requirement.
 * Value: 1.5
 */
const VERY_HIGH_SIMILARITY_BYPASS_MULTIPLIER = 1.5;

/**
 * Dominant frequency similarity multiplier for very high similarity bypass.
 * Requires dominant frequency similarity to be 120% of minimum threshold for bypass.
 * Value: 1.2
 */
const DOMINANT_FREQ_BYPASS_MULTIPLIER = 1.2;

/**
 * Maximum frame offset for mobile devices when aligning audio spectra.
 * Used to find best alignment between reference and recorded audio.
 * Value: 30 frames
 */
const MAX_FRAME_OFFSET_MOBILE = 30;

/**
 * Maximum frame offset for desktop devices when aligning audio spectra.
 * Used to find best alignment between reference and recorded audio.
 * Value: 20 frames
 */
const MAX_FRAME_OFFSET_DESKTOP = 20;

/**
 * Sample rate difference threshold in Hz.
 * If reference and recorded sample rates differ by more than this value, resampling is performed.
 * Value: 100 Hz
 */
const SAMPLE_RATE_DIFFERENCE_THRESHOLD = 100;

/**
 * Minimum number of top frequency matches required for low mobile threshold scenarios.
 * At least 3 dominant frequencies must match between reference and recorded audio.
 * Value: 3
 */
const MIN_TOP_FREQUENCY_MATCHES_LOW_MOBILE = 3;

/**
 * Minimum number of top frequency matches required for standard mobile scenarios.
 * At least 4 dominant frequencies must match between reference and recorded audio.
 * Value: 4
 */
const MIN_TOP_FREQUENCY_MATCHES_MOBILE = 4;

/**
 * Minimum number of top frequency matches required for desktop scenarios.
 * At least 5 dominant frequencies must match between reference and recorded audio.
 * Value: 5
 */
const MIN_TOP_FREQUENCY_MATCHES_DESKTOP = 5;

/**
 * Minimum number of top frequency matches required for Safari browsers.
 * Safari's audio processing affects frequencies differently, so we use a more lenient threshold.
 * At least 2 dominant frequencies must match between reference and recorded audio.
 * Value: 2
 */
const MIN_TOP_FREQUENCY_MATCHES_SAFARI = 2;

/**
 * Frequency tolerance in Hz for mobile devices when comparing dominant frequencies.
 * Frequencies within this range are considered matching.
 * Value: 15 Hz
 */
const FREQUENCY_TOLERANCE_MOBILE = 15;

/**
 * Frequency tolerance in Hz for desktop devices when comparing dominant frequencies.
 * Frequencies within this range are considered matching.
 * Value: 8 Hz
 */
const FREQUENCY_TOLERANCE_DESKTOP = 8;

/**
 * Number of dominant frequencies to find and compare between reference and recorded audio.
 * Used to identify the most prominent frequency components for similarity analysis.
 * Value: 20 frequencies
 */
const NUM_DOMINANT_FREQUENCIES_TO_FIND = 20;

/**
 * Returns all waveform comparison constants as an object.
 * Destructure the needed constants from this function's return value.
 */
export function getWaveformComparisonConstants() {
  return {
    AVG_SPECTRUM_WEIGHT_DESKTOP,
    AVG_SPECTRUM_WEIGHT_MOBILE,
    DEFAULT_SIMILARITY_THRESHOLD,
    DOMINANT_FREQUENCY_ENERGY_THRESHOLD,
    DOMINANT_FREQUENCY_RELATIVE_THRESHOLD_MULTIPLIER,
    DOMINANT_FREQUENCY_WEIGHT_DESKTOP,
    DOMINANT_FREQUENCY_WEIGHT_MOBILE,
    FRAME_SIMILARITY_WEIGHT_DESKTOP,
    FRAME_SIMILARITY_WEIGHT_MOBILE,
    FREQUENCY_TOLERANCE_DESKTOP,
    FREQUENCY_TOLERANCE_MOBILE,
    HAMMING_WINDOW_CONSTANT,
    HAMMING_WINDOW_COSINE_COEFFICIENT,
    LOW_MOBILE_THRESHOLD_CUTOFF,
    MAX_FRAME_OFFSET_DESKTOP,
    MAX_FRAME_OFFSET_MOBILE,
    MAX_RMS_RATIO_LOW_MOBILE,
    MAX_RMS_RATIO_SAFARI,
    MAX_RMS_RATIO_STANDARD,
    MIN_AVG_SPECTRUM_SIMILARITY_DESKTOP,
    MIN_AVG_SPECTRUM_SIMILARITY_LOW_MOBILE,
    MIN_AVG_SPECTRUM_SIMILARITY_MOBILE,
    MIN_DOMINANT_FREQUENCY_SIMILARITY_DESKTOP,
    MIN_DOMINANT_FREQUENCY_SIMILARITY_LOW_MOBILE,
    MIN_DOMINANT_FREQUENCY_SIMILARITY_MOBILE,
    MIN_DURATION_RATIO_DESKTOP,
    MIN_DURATION_RATIO_LOW_MOBILE,
    MIN_DURATION_RATIO_MOBILE,
    MIN_FRAME_SIMILARITY_DESKTOP,
    MIN_FRAME_SIMILARITY_LOW_MOBILE,
    MIN_FRAME_SIMILARITY_MOBILE,
    MIN_MOBILE_SIMILARITY_THRESHOLD,
    MIN_OVERALL_SIMILARITY,
    MIN_RMS_FALLBACK,
    MIN_RMS_RATIO_LOW_MOBILE,
    MIN_RMS_RATIO_SAFARI,
    MIN_RMS_RATIO_STANDARD,
    MIN_RMS_THRESHOLD_DESKTOP,
    MIN_RMS_THRESHOLD_MOBILE,
    MIN_RMS_THRESHOLD_SAFARI,
    MIN_TOP_FREQUENCY_MATCHES_DESKTOP,
    MIN_TOP_FREQUENCY_MATCHES_LOW_MOBILE,
    MIN_TOP_FREQUENCY_MATCHES_MOBILE,
    MIN_TOP_FREQUENCY_MATCHES_SAFARI,
    MOBILE_SIMILARITY_THRESHOLD_ADJUSTMENT,
    NOISE_DETECTION_SIMILARITY_MULTIPLIER,
    NUM_DOMINANT_FREQUENCIES_TO_FIND,
    REASONABLE_SIMILARITY_EARLY_RETURN_MULTIPLIER,
    REASONABLE_SIMILARITY_FINAL_CHECK_MULTIPLIER,
    SAFARI_HIGH_SIMILARITY_THRESHOLD,
    SAFARI_RMS_FALLBACK_MULTIPLIER,
    SAMPLE_RATE_DIFFERENCE_THRESHOLD,
    DOMINANT_FREQ_SIMILARITY_LOW_THRESHOLD_DESKTOP,
    DOMINANT_FREQ_SIMILARITY_LOW_THRESHOLD_MOBILE,
    DOMINANT_FREQ_SIMILARITY_NORMAL_THRESHOLD_DESKTOP,
    DOMINANT_FREQ_SIMILARITY_NORMAL_THRESHOLD_MOBILE,
    DOMINANT_FREQ_BYPASS_MULTIPLIER,
    FFT_SIZE,
    LOW_THRESHOLD_DURATION_RATIO_MULTIPLIER,
    LOW_THRESHOLD_TOP_FREQ_MATCHES_MULTIPLIER,
    MIN_COMPONENT_SIMILARITY_FLOOR_LOW_THRESHOLD,
    MIN_DURATION_RATIO_FLOOR_LOW_THRESHOLD,
    MIN_THRESHOLD_SCALE_FOR_LOW_THRESHOLD,
    MIN_TOP_FREQUENCY_MATCHES_FLOOR_LOW_THRESHOLD,
    MIN_TOP_FREQUENCY_MATCHES_REQUIRED_LOW_THRESHOLD,
    MIN_TOP_MATCHES_FOR_TOP2_VALIDATION,
    TOP_FREQUENCIES_COUNT_FOR_COMPARISON,
    TOP_FREQUENCIES_COUNT_FOR_VALIDATION,
    VERY_HIGH_SIMILARITY_BYPASS_MULTIPLIER,
  };
}
